<template>
  <div
    class="drag-drop-unused-option"
    :class="{
      'image-option': isImageOption,
      'text-option': !isImageOption,
    }"
    :style="optionStyle"
  >
    <slot name="label" />
    <img v-if="isImageOption" class="image" :src="option.src" />
    <div v-else class="text">
      {{ option.textValue }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import DeviceUtils from "@utils/device.utils";

interface Option {
  height: number;
  width: number;
  fileName: string;
  textValue: string;
  src?: string;
  [key: string]: unknown;
}

interface Props {
  option?: Option;
  isAggregateAttempt?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isAggregateAttempt: false,
  option: () => ({
    height: 0,
    width: 0,
    fileName: "",
    textValue: "",
  }),
});

const isImageOption = computed(() => props.option && props.option.fileName);

// Rescale the option if it exceeds the max width/height to maintain the aspect ratio
// Max-width for Mobile, Tablet, and Desktop will be 200px, 400px and 600px respectively,
// so that it doesn't overlap with the edit button
// Max-height will be 200px for better UX to allow the user to view more options within the viewport
const optionStyle = computed(() => {
  if (props.isAggregateAttempt) {
    return false;
  }

  const maxWidth = DeviceUtils.isMobile() ? 200 : DeviceUtils.isTablet() ? 400 : 600;
  const maxHeight = 200;
  const dimension = {
    width: props.option.width,
    height: props.option.height,
  };
  const aspectRatio = dimension.width / dimension.height; // 500 / 250 = 2, 1200 / 200 = 6, 1800 / 400 = 3

  // For scenario whereby the height and width both exceeds the width, need to determine which to scale to
  // If the ratio is greater than 1, that means the dimension has exceeded the max
  const dimWidthToMaxWidthRatio = dimension.width / maxWidth;
  const dimHeightToMaxHeightRatio = dimension.height / maxHeight;

  if (dimHeightToMaxHeightRatio > 1) {
    if (dimWidthToMaxWidthRatio < dimHeightToMaxHeightRatio) {
      dimension.height = maxHeight;
      dimension.width = dimension.height * aspectRatio;
    } else {
      dimension.width = maxWidth;
      dimension.height = dimension.width / aspectRatio;
    }
  } else if (dimWidthToMaxWidthRatio > 1) {
    dimension.width = maxWidth;
    dimension.height = dimension.width / aspectRatio;
  }

  return {
    width: `${dimension.width}px`,
    height: `${dimension.height}px`,
  };
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.drag-drop-unused-option {
  display: flex;
  align-items: center;
  margin: $spacing-03 auto;
  background-color: $ui-01;
  border: 1px solid $ui-04;
  text-align: center;
  max-height: 200px;

  &.text-option {
    padding: $spacing-05;
  }

  .image {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  .text {
    width: 100%;
    text-align: center;
    word-break: break-word;
  }
}
</style>
